import {calculateApneaScores, getIsHighOsaRisk} from 'screens/InsomniaSurvey/utils/apneaScoring';
import {getISIScore} from 'screens/InsomniaSurvey/utils/isiScoring';
import {getPayerIdByValue} from 'surveys/insomnia_funnel/paywall/utils';
import {
  SleepioAllowedPayersByState,
  SleepioIsiSnapshot,
  SleepioOsaFollowUp,
  SleepioOsaFollowUpDerived,
  SleepioOsaRisk,
} from './types';

const ISI_QUESTIONS: Record<string, string> = {
  insomnia_falling_asleep: 'How difficult is it for you to FALL asleep?',
  insomnia_staying_asleep: 'How difficult is it for you to STAY asleep?',
  insomnia_waking_up: 'Rate your problem with waking up too early',
  insomnia_daily_functioning: 'Does your sleep interfere with your daily functioning?',
  insomnia_sleep_pattern: 'How satisfied are you with your sleep pattern?',
  insomnia_noticeable_to_others: 'How noticeable do you think your sleep problems are to others?',
  insomnia_worried: 'How worried/distressed are you about your sleep?',
};

const ISI_LABELS: Record<string, Record<number, string>> = {
  insomnia_falling_asleep: {
    0: 'Not at all difficult',
    1: 'A little difficult',
    2: 'Somewhat difficult',
    3: 'Difficult',
    4: 'Very difficult',
  },
  insomnia_staying_asleep: {
    0: 'Not at all difficult',
    1: 'A little difficult',
    2: 'Somewhat difficult',
    3: 'Difficult',
    4: 'Very difficult',
  },
  insomnia_waking_up: {
    0: 'Not an issue',
    1: 'Mild',
    2: 'Moderate',
    3: 'Severe',
    4: 'Very severe',
  },
  insomnia_daily_functioning: {
    0: 'Not at all',
    1: 'Barely',
    2: 'Somewhat',
    3: 'Much',
    4: 'Very much interfering',
  },
  insomnia_sleep_pattern: {
    0: 'Very satisfied',
    1: 'Satisfied',
    2: 'Moderately satisfied',
    3: 'Dissatisfied',
    4: 'Very dissatisfied',
  },
  insomnia_noticeable_to_others: {
    0: 'Not at all noticeable',
    1: 'A little',
    2: 'Somewhat',
    3: 'Much',
    4: 'Very much noticeable',
  },
  insomnia_worried: {
    0: 'Not at all worried',
    1: 'A little',
    2: 'Somewhat',
    3: 'Much',
    4: 'Very much worried',
  },
};

export const normalizePhone = (phone: string): string => phone.replace(/\D/g, '').replace(/^1/, '');

// Accepts flexible US date entry: 1-2 digit month/day and a 2- or 4-digit year —
// e.g. '4/12/1992', '04/12/92', '4/9/92', '04/12/1992'. Two-digit years use a
// sliding window: a year that would land in the future maps to the previous
// century ('92' -> 1992, '04' -> 2004). Always returns a zero-padded 'yyyy-MM-dd'
// wire string.
export const convertDateToISODate = (date: string): string => {
  // Normalize separators ('-', '.', whitespace) to '/' so direct callers are as
  // tolerant as the masked input field.
  const normalized = date.trim().replace(/[.\-\s]+/g, '/');
  const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!match) {
    throw new Error('Please enter your date of birth as MM/DD/YYYY.');
  }

  const [, monthRaw, dayRaw, yearRaw] = match;
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  let year = Number(yearRaw);

  if (yearRaw.length === 2) {
    const currentYear = new Date().getFullYear();
    year += Math.floor(currentYear / 100) * 100; // assume current century first
    if (year > currentYear) {
      year -= 100; // a future date means they meant the previous century
    }
  }

  const parsed = new Date(year, month - 1, day);
  const isValid = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;

  if (!isValid) {
    throw new Error('Please enter a valid date of birth.');
  }

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

export const getAgeFromDob = (date: string, now = new Date()): number => {
  const iso = convertDateToISODate(date);
  const [year, month, day] = iso.split('-').map(Number);
  let age = now.getFullYear() - year;
  const birthdayThisYear = new Date(now.getFullYear(), month - 1, day);
  if (now < birthdayThisYear) {
    age -= 1;
  }
  return age;
};

export const assertAdultDob = (date: string) => {
  if (getAgeFromDob(date) < 18) {
    throw new Error('SleepioRx is available for adults 18 and older.');
  }
};

export const isSleepioAllowedForStateAndPayer = (
  matrix: SleepioAllowedPayersByState,
  state?: string,
  payerValue?: string,
): boolean => {
  if (!state || !payerValue) {
    return false;
  }

  const payerId = getPayerIdByValue(payerValue);
  if (!payerId) {
    return false;
  }

  const payersForState = matrix[state.toUpperCase()] ?? matrix[state.toLowerCase()];
  return Array.isArray(payersForState) && payersForState.includes(payerId);
};

const getIsiCategory = (score: number): SleepioIsiSnapshot['category'] => {
  if (score <= 7) return 'no_clinically_significant';
  if (score <= 14) return 'subthreshold';
  if (score <= 21) return 'clinical_moderate';
  return 'clinical_severe';
};

export const buildIsiSnapshot = (surveyData: Record<string, any>): SleepioIsiSnapshot | undefined => {
  const answers = Object.keys(ISI_QUESTIONS)
    .map(id => {
      const value = Number(surveyData[id]);
      if (!Number.isFinite(value)) {
        return null;
      }
      return {
        id,
        question: ISI_QUESTIONS[id],
        value,
        label: ISI_LABELS[id]?.[value],
      };
    })
    .filter((answer): answer is NonNullable<typeof answer> => answer !== null);

  if (answers.length === 0) {
    return undefined;
  }

  const total = getISIScore();
  return {
    answers,
    total,
    category: getIsiCategory(total),
    takenAt: surveyData.sleepio_isi_taken_at ?? new Date().toISOString(),
  };
};

const yes = (value: unknown): boolean => value === 'Yes';

const ageYearsFromDob = (dob: unknown): number | undefined => {
  if (typeof dob !== 'string' || !dob.trim()) {
    return undefined;
  }
  try {
    return getAgeFromDob(dob);
  } catch {
    return undefined;
  }
};

const ageYearsFromRange = (age: unknown): number | undefined => {
  switch (age) {
    case '18-23':
      return 21;
    case '24-29':
      return 27;
    case '30s':
      return 35;
    case '40s':
      return 45;
    case '50s':
      return 55;
    case '60+':
      return 65;
    default:
      return undefined;
  }
};

type OsaFollowUpAnswers = Pick<SleepioOsaFollowUp, 'diagnosed' | 'treatment'>;

/**
 * Collapse the OSA follow-up answers into the signal the provider note + downstream
 * referral key off (mirrors nyx `deriveSleepioOsaFollowUpState`). Only an explicit
 * `yes` diagnosis paired with a real treatment counts as `treated`; everything else
 * (incl. `not_sure` + a treatment) is `untreated_or_unknown`.
 */
export const deriveSleepioOsaFollowUpState = (followUp: OsaFollowUpAnswers): SleepioOsaFollowUpDerived => {
  const isTreated =
    followUp.diagnosed === 'yes' && followUp.treatment !== undefined && followUp.treatment !== 'not_treated';
  return isTreated ? 'treated' : 'untreated_or_unknown';
};

export const buildOsaRisk = (
  surveyData: Record<string, any>,
  followUpAnswers?: OsaFollowUpAnswers,
): SleepioOsaRisk | undefined => {
  // Build whenever we have any apnea signal — either scores persisted by the silent
  // scorer (bmi/apnealink/au) OR the raw height/weight inputs (calculateApneaScores
  // re-derives BMI + scores from raw answers below).
  if (
    surveyData.bmi === undefined &&
    surveyData.apnealink_score === undefined &&
    surveyData.au_osa_score === undefined &&
    surveyData.height_feet === undefined &&
    surveyData.weight_pounds === undefined
  ) {
    return undefined;
  }

  const scores = calculateApneaScores(surveyData);
  const bmi = scores.bmi ?? Number(surveyData.bmi);
  if (!Number.isFinite(bmi)) {
    return undefined;
  }

  // Follow-up is present only for high-risk users who answered (at least diagnosed).
  const followUp: SleepioOsaFollowUp | undefined =
    getIsHighOsaRisk(surveyData) && followUpAnswers?.diagnosed
      ? {
          diagnosed: followUpAnswers.diagnosed,
          ...(followUpAnswers.treatment ? {treatment: followUpAnswers.treatment} : {}),
          derived: deriveSleepioOsaFollowUpState(followUpAnswers),
        }
      : undefined;

  return {
    ...(followUp ? {followUp} : {}),
    surveyResponses: {
      // Round to 1 decimal on the wire — strips float noise (e.g. 31.32100…).
      bmi: Math.round(bmi * 10) / 10,
      snoreLoudly: yes(surveyData.apnea_snore),
      stoppedBreathingDuringSleep: yes(surveyData.apnea_stop_breathing),
      tiredOrFatigued: yes(surveyData.apnea_daytime_sleepy),
      gasping: yes(surveyData.apnea_gasping),
      headaches: yes(surveyData.apnea_morning_headaches),
      pain: surveyData.apnea_sleep_pain_frequency,
      dryMouth: yes(surveyData.apnea_dry_mouth),
      highBloodPressure: yes(surveyData.apnea_high_blood_pressure),
    },
    scores: {
      australia: Number(surveyData.au_osa_score ?? scores.auScore),
      // stopBang isn't computed by this screener; include only if a value exists.
      ...(surveyData.stop_bang_score !== undefined ? {stopBang: Number(surveyData.stop_bang_score)} : {}),
    },
    demographics: {
      // Prefer the real DOB collected in the SleepioRx flow; fall back to the coarse
      // onboarding age range if DOB is absent or unparseable.
      ageYears: ageYearsFromDob(surveyData.dob) ?? ageYearsFromRange(surveyData.age),
      gender: typeof surveyData.gender === 'string' ? surveyData.gender.toLowerCase() : undefined,
    },
  };
};
